import { Injectable, signal, computed, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, map, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { SocialAuthService } from '@abacritt/angularx-social-login';
import { CandidateProfile, Gender, UserProfile, UserRole } from '../models/user-profile.model';
import { AuthenticationResponseDto } from '../models/auth.model';
import { mapAuthResponseToProfile, mapBackendRole, mapCandidateProfileDtoToProfile, mapDomainToUpdateRequest } from '../mappers/auth.mapper';
import { AuthApiService } from './auth-api.service';
import { CandidatePortalApiService } from './candidate-portal-api.service';
import { InviteApiService } from './invite-api.service';
import { AcceptInviteRequestDto } from '../models/invite.model';
import { roleHomePath } from '../../../shared/utils/role-home.util';

const STORAGE_KEY = 'tm_candidate_profile';
const TOKEN_KEY = 'tm_access_token';
/**
 * Korisnik je prijavljen privremenom lozinkom iz maila i mora postaviti svoju.
 * Ide u localStorage, ne samo u signal: bez toga bi osvježavanje stranice ili
 * ručno upisan /dashboard zaobišli postavljanje.
 */
const MUST_SET_PASSWORD_KEY = 'tm_must_set_password';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly authApi = inject(AuthApiService);
  private readonly portalApi = inject(CandidatePortalApiService);
  private readonly inviteApi = inject(InviteApiService);
  private readonly socialAuthService = inject(SocialAuthService);
  private readonly user = signal<UserProfile | null>(null);
  private readonly token = signal<string | null>(null);
  private readonly mustSetPasswordFlag = signal(false);
  private refreshInProgress$: Observable<string> | null = null;

  readonly isLoggedIn = computed(() => !!this.user());
  readonly currentUser = this.user.asReadonly();
  readonly accessToken = this.token.asReadonly();

  readonly initials = computed(() => {
    const u = this.user();
    if (!u?.name) return '';
    return u.name
      .split(' ')
      .map(w => w.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  });

  readonly companyId = computed<number | null>(() => {
    const u = this.user();
    return u && u.role !== 'candidate' ? (u.companyId ?? null) : null;
  });

  readonly companyName = computed<string | null>(() => {
    const u = this.user();
    return u && u.role !== 'candidate' ? u.companyName : null;
  });

  readonly gender = computed<Gender>(() => this.user()?.gender ?? 'male');
  readonly role = computed<UserRole>(() => this.user()?.role ?? 'candidate');

  /** True dok korisnik ne zamijeni privremenu lozinku vlastitom. */
  readonly mustSetPassword = this.mustSetPasswordFlag.asReadonly();

  private redirectByRole(role: UserRole): void {
    this.router.navigate(roleHomePath(role));
  }

  /** Zovi nakon uspješnog POST /auth/change-password. */
  clearMustSetPassword(): void {
    this.mustSetPasswordFlag.set(false);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(MUST_SET_PASSWORD_KEY);
    }
  }

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try { this.user.set(JSON.parse(stored)); } catch { /* ignore corrupt data */ }
      }
      this.token.set(localStorage.getItem(TOKEN_KEY));
      this.mustSetPasswordFlag.set(localStorage.getItem(MUST_SET_PASSWORD_KEY) === '1');
    }
  }

  /**
   * Email login. Vraća COLD Observable — caller MORA subscribe-ati
   * (inače se request ne pošalje). Na uspjeh: token + profil + redirect.
   */
  loginWithEmail(email: string, password: string): Observable<void> {
    return this.authApi.authenticate({ email, password }).pipe(
      tap(dto => this.handleAuthSuccess(dto, email)),
      switchMap(dto => this.loadFullProfile(mapBackendRole(dto.role))),
      map(() => undefined),
    );
  }

  /**
   * Google login. Vraća COLD Observable — caller MORA subscribe-ati.
   * Email mora proslijediti caller jer backend response ga ne vraća
   * (caller ga ima iz SocialUser-a kojeg lib dekodira iz idToken-a).
   */
  loginWithGoogle(idToken: string, email: string): Observable<void> {
    return this.authApi.authenticateGoogle(idToken).pipe(
      tap(dto => this.handleAuthSuccess(dto, email)),
      switchMap(dto => this.loadFullProfile(mapBackendRole(dto.role))),
      map(() => undefined),
    );
  }

  /**
   * Facebook login. Vraća COLD Observable — caller MORA subscribe-ati.
   * Email i accessToken caller dobije iz SocialUser-a kojeg lib emitira
   * iz `socialAuthService.authState`.
   */
  loginWithFacebook(accessToken: string, email: string): Observable<void> {
    return this.authApi.authenticateFacebook(accessToken).pipe(
      tap(dto => this.handleAuthSuccess(dto, email)),
      switchMap(dto => this.loadFullProfile(mapBackendRole(dto.role))),
      map(() => undefined),
    );
  }

  /**
   * Candidate signup. Backend radi auto-login → isto rukovanje kao login.
   * Vraća COLD Observable — caller MORA subscribe-ati.
   */
  signup(firstname: string, lastname: string, email: string, password: string): Observable<void> {
    return this.authApi.registerCandidate({ firstname, lastname, email, password }).pipe(
      tap(dto => this.handleAuthSuccess(dto, email)),
      map(() => undefined),
    );
  }

  /**
   * OTP step 1 — zatraži SMS na telefon. Vraća COLD Observable —
   * caller MORA subscribe-ati. NE mijenja state (token/profil/redirect
   * se događaju tek u loginWithOtp).
   */
  requestOtp(phone: string): Observable<void> {
    return this.authApi.sendOtp({ phoneNumber: phone }).pipe(
      map(() => undefined),
    );
  }

  /**
   * OTP step 2 — provjeri kod i logiraj. Vraća COLD Observable.
   * Mirror email/Google/Facebook pattern: handleAuthSuccess postavi token +
   * minimal profile + redirect, zatim loadFullProfile rehidrira pun profil
   * iz baze (BEZ ovog ostaje email: '', experience: '' itd.).
   * Telefon prosljeđujemo u handleAuthSuccess jer auth response ne vraća
   * ni email ni phone — telefon u minimal profile-u je samo bridge dok
   * loadFullProfile ne overwrite-a profil iz baze.
   */
  loginWithOtp(phone: string, code: string): Observable<void> {
    return this.authApi.verifyOtp({ phoneNumber: phone, otpCode: code }).pipe(
      tap(dto => this.handleAuthSuccess(dto, '', phone)),
      switchMap(dto => this.loadFullProfile(mapBackendRole(dto.role))),
      map(() => undefined),
    );
  }

  /**
   * Prihvati company invite — kreira račun u firmi i odmah logira (backend
   * vraća isti AuthenticationResponse kao login). Vraća COLD Observable —
   * caller MORA subscribe-ati. `email` dolazi iz invite metadate jer ga auth
   * response ne vraća. handleAuthSuccess postavi token + profil i preusmjeri
   * po roli (recruiter → /dashboard). Bez loadFullProfile — pozvani korisnik
   * je staff rola, ne candidate.
   */
  acceptInvite(token: string, body: AcceptInviteRequestDto, email: string): Observable<void> {
    return this.inviteApi.accept(token, body).pipe(
      tap(dto => this.handleAuthSuccess(dto, email)),
      map(() => undefined),
    );
  }

  /**
   * Update candidate profila. Vraća COLD Observable — caller MORA subscribe-ati.
   * Local merge konstruira request DTO; što sleće u state je backend response
   * (source of truth) preko mappera koji re-derivira gender i preserve-a role.
   *
   * CV polja (hasCv/cvFileName/cvUploadedAt) NISU u ovom formu — vlasništvo su
   * zasebnih /my-profile/cv endpointa. Preserve-amo ih iz `merged` (= trenutni
   * state) da Spremi promjene nikad ne obriše CV, neovisno o tome vraća li ih PUT.
   */
  updateProfile(partial: Partial<CandidateProfile>): Observable<CandidateProfile> {
    const current = this.user();
    if (!current || current.role !== 'candidate') return throwError(() => new Error('Not a candidate'));

    const merged: CandidateProfile = { ...current, ...partial };
    const dto = mapDomainToUpdateRequest(merged);

    return this.portalApi.updateProfile(dto).pipe(
      map(response => ({
        ...mapCandidateProfileDtoToProfile(response, 'candidate'),
        hasCv: merged.hasCv ?? false,
        cvFileName: merged.cvFileName ?? null,
        cvUploadedAt: merged.cvUploadedAt ?? null,
      })),
      tap(profile => this.setUser(profile)),
    );
  }

  /**
   * Re-fetch /my-profile i resync user signala. Public — koristi se nakon
   * CV upload/delete da pokupi kanonski cvFileName/cvUploadedAt (upload
   * response ih ne vraća). Swallow-a grešku kao loadFullProfile (poziva se tek
   * nakon već uspješne CV operacije → optimistic state ostaje ako GET padne).
   */
  reloadProfile(): Observable<unknown> {
    const current = this.user();
    if (!current || current.role !== 'candidate') return of(undefined);
    return this.portalApi.getMyProfile().pipe(
      tap(dto => {
        const cur = this.user();
        if (!cur || cur.role !== 'candidate') return;
        this.setUser(mapCandidateProfileDtoToProfile(dto, 'candidate'));
      }),
      catchError(() => of(undefined)),
    );
  }

  logout(): void {
    this.clearSession();
    this.router.navigate(['/']);
  }

  /**
   * Odjava bez navigacije: lokalno stanje i tokeni nestaju, stranica ostaje.
   * Treba nakon brisanja računa — korisnik na istom ekranu dobije potvrdu da je
   * račun obrisan, a da pritom nijedan daljnji poziv ne ode s tokenom koji više
   * ne vrijedi (refresh bi inače pokušao obnoviti sesiju obrisanog korisnika).
   */
  clearSession(): void {
    this.user.set(null);
    this.token.set(null);
    // I zastavica prve prijave — inače bi sljedeći korisnik na istom pregledniku
    // odmah završio na postavljanju lozinke.
    this.clearMustSetPassword();
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(TOKEN_KEY);
    }
    // Čisti cache GIS lib-a — bez ovog bi se SocialUser cached state
    // mogao re-emitirati pri povratku na /login i automatski relogin-ati.
    this.socialAuthService.signOut().catch(() => { /* ignore: lib baca ako nije signed-in */ });
  }

  refreshAccessToken(): Observable<string> {
    if (this.refreshInProgress$) return this.refreshInProgress$;

    this.refreshInProgress$ = this.authApi.refreshToken().pipe(
      tap(dto => {
        if (isPlatformBrowser(this.platformId)) {
          localStorage.setItem(TOKEN_KEY, dto.token);
        }
        this.token.set(dto.token);
      }),
      map(dto => dto.token),
      shareReplay(1),
      finalize(() => { this.refreshInProgress$ = null; }),
    );
    return this.refreshInProgress$;
  }

  /** Spremi token, izgradi profil iz auth response-a, postavi usera i preusmjeri po roli.
   *  `phone` je 3. pozicijski optional za OTP flow — postojeći callers prosljeđuju samo email. */
  /**
   * Magic-link prijava tokenom iz e-maila (potvrda prijave na oglas).
   * Vraca `candidateId` prijave iz linka da pozivatelj moze otvoriti bas nju.
   * COLD Observable — caller MORA subscribe-ati.
   *
   * `redirect: false`: kamo dalje odlucuje stranica, ne servis — cilj je
   * konkretna prijava, a ne home rute role. Iznimka je privremena lozinka,
   * koju handleAuthSuccess i dalje presretne (kandidat je nikad nema, ali
   * pravilo vrijedi za sve ulaze).
   */
  loginWithMagicLink(token: string): Observable<number | null> {
    return this.authApi.magicLinkLogin(token).pipe(
      tap(res => this.handleAuthSuccess(res.authDTO, '', '', false)),
      switchMap(res => this.loadFullProfile(mapBackendRole(res.authDTO.role)).pipe(
        map(() => res.candidateId ?? null),
      )),
    );
  }

  private handleAuthSuccess(dto: AuthenticationResponseDto, email: string = '', phone: string = '',
                            redirect: boolean = true): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(TOKEN_KEY, dto.token);
    }
    this.token.set(dto.token);
    const profile = mapAuthResponseToProfile(dto, email, phone);
    this.setUser(profile);

    // Privremena lozinka iz maila: vodi na postavljanje umjesto na dashboard.
    // Dosad V3 ovo polje nije ni čitao, pa se privremena lozinka nikad nije mijenjala.
    if (dto.firstLogin) {
      this.mustSetPasswordFlag.set(true);
      if (isPlatformBrowser(this.platformId)) {
        localStorage.setItem(MUST_SET_PASSWORD_KEY, '1');
      }
      this.router.navigate(['/postavi-lozinku']);
      return;
    }

    this.clearMustSetPassword();
    if (redirect) this.redirectByRole(profile.role);
  }

  /**
   * Chained nakon authenticate-a u login flow-ovima. Rehidrira profil iz
   * baze samo za candidate role. GET failure swallow-a → login se i dalje
   * uspješno završi s minimal profile-om (NE blokira login).
   */
  private loadFullProfile(role: UserRole): Observable<unknown> {
    if (role !== 'candidate') return of(undefined);
    return this.portalApi.getMyProfile().pipe(
      tap(dto => {
        const current = this.user();
        if (!current || current.role !== 'candidate') return;
        const profile = mapCandidateProfileDtoToProfile(dto, 'candidate');
        this.setUser(profile);
      }),
      catchError(() => of(undefined)),
    );
  }

  private setUser(profile: UserProfile): void {
    this.user.set(profile);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    }
  }
}
